import { fetchOpenAIText } from "../utils/openaiClient";

export function useGrammarRulesGeneratorLogic({
  grammarRulesInput, setGrammarRulesInput, grammarRulesLevel, grammarRulesLanguage,
  setGrammarRulesLoading, generatedGrammarRules, setGeneratedGrammarRules,
  currentTab, onInsertToBoard, activeEditor,
  setShowGrammarRulesGenerator, setSelectedTemplate,
}) {
  const handleGrammarRulesGenerate = async () => {
    if (!grammarRulesInput.trim()) return;

    setGrammarRulesLoading(true);
    try {
      const levelGuidance = {
        'A1': 'Use extremely simple words. Short sentences only. No grammar terminology — describe everything in plain everyday language. Maximum 3–4 examples. One or two sections only (What it is + Examples). Avoid any abstract explanation.',
        'A2': 'Use simple, everyday vocabulary. Minimal grammar terms — when used, immediately explain them in plain words. 3–5 examples. Keep sections short and concrete. Focus on the most common use case only.',
        'B1': 'Use clear, accessible language. Introduce standard grammar terms with brief explanations. 4–6 examples covering common situations. Cover formation, main usage, and the most frequent mistakes. Balanced depth.',
        'B2': 'Use accurate linguistic terminology. Explain nuances and multiple use cases. 5–7 varied examples including edge cases. Cover register (formal/informal). Include common errors with analysis.',
        'C1': 'Use precise academic language. Cover all major uses including subtle distinctions, exceptions, and stylistic variation. 6–8 examples including sophisticated contexts. Contrast with similar structures.',
        'C2': 'Use expert-level metalanguage. Provide exhaustive coverage: all uses, exceptions, stylistic nuances, formal vs. informal register, and contrast with near-synonymous structures. Rich, authentic examples.',
      };

      const levelInstruction = levelGuidance[grammarRulesLevel] || levelGuidance['B1'];

      const prompt = `Create a grammar lesson for ${grammarRulesLevel} level students.
Write ALL explanations, headings, and labels in ${grammarRulesLanguage}. English examples should stay in English but be accompanied by a ${grammarRulesLanguage} translation or note where helpful for this level.

TOPIC: ${grammarRulesInput}

LEVEL INSTRUCTIONS (${grammarRulesLevel}):
${levelInstruction}

STRUCTURE — adapt depth and number of sections to the level:
- For A1/A2: keep it to 2–3 short sections (What is it + Examples + Quick tip)
- For B1/B2: use 4–5 sections (Introduction, How to form it, When to use it, Examples, Common mistakes)
- For C1/C2: use 5–6 sections (Overview, Formation & variations, All usage contexts, Advanced examples, Common errors & why, Quick reference)

LANGUAGE-SPECIFIC GUIDANCE:
- If the instruction language is Russian or Armenian, highlight where English grammar differs from the student's native language (e.g. articles, aspect, word order)
- If the instruction language is Spanish, French, Italian, or Portuguese, draw analogies to Romance language structures where helpful
- If the instruction language is German, note parallels and differences in case/verb structure
- For other languages, briefly contrast the target English structure with what students are likely used to

FORMATTING:
- Use ONLY h2 and h3 headings (never h1)
- Use <strong> for grammar terms and key words
- Use <em> for emphasis
- Use <ul><li> for lists
- Use <blockquote> for important tips or warnings
- Use <code> for short grammar formulas (e.g. <code>Subject + have/has + past participle</code>)
- NEVER use markdown, backticks, or code fences — clean HTML only

OUTPUT: clean HTML, no wrapping tags, no code block markers`;

      const systemPrompt = `You are an expert English language teacher with 20+ years of experience teaching students from diverse linguistic backgrounds. You write grammar lessons in ${grammarRulesLanguage} that are perfectly calibrated for ${grammarRulesLevel} level — never too simple, never too complex. Your explanations are crystal clear, your examples are memorable and real-world, and your HTML formatting makes content easy to scan and study.`;

      const fullPrompt = `${systemPrompt}\n\n${prompt}`;

      const content = await fetchOpenAIText({
        prompt: fullPrompt,
        temperature: 0.6,
        maxTokens: 2500,
        model: 'gpt-4',
      });

      if (content) {
        setGeneratedGrammarRules(content.trim());
      }
    } catch (e) {
      // Error generating grammar rules
    } finally {
      setGrammarRulesLoading(false);
    }
  };

  const handleGrammarRulesInsert = () => {
    if (currentTab > 0 && onInsertToBoard && generatedGrammarRules) {
      const boardData = {
        content: generatedGrammarRules,
        title: grammarRulesInput,
        level: grammarRulesLevel,
      };
      onInsertToBoard(boardData, 'grammarrules');
      setShowGrammarRulesGenerator(false);
      setGrammarRulesInput("");
      setGeneratedGrammarRules(null);
      setSelectedTemplate(null);
      return;
    }
    if (generatedGrammarRules && activeEditor) {
      activeEditor.chain().focus().insertGrammarRulesExercise({
        content: generatedGrammarRules,
        title: grammarRulesInput,
        level: grammarRulesLevel,
      }).run();
    }
    setShowGrammarRulesGenerator(false);
    setGrammarRulesInput("");
    setGeneratedGrammarRules(null);
    setSelectedTemplate(null);
  };

  const handleGrammarRulesCancel = () => {
    setShowGrammarRulesGenerator(false);
    setGrammarRulesInput("");
    setGeneratedGrammarRules(null);
    setSelectedTemplate(null);
  };

  return { handleGrammarRulesGenerate, handleGrammarRulesInsert, handleGrammarRulesCancel };
}
